import { Component, OnInit, ElementRef, Input } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { SubscriptionService } from '../../../services/subscription.service';
import { ChangeCollectionTypeService } from '../../../services/change-collection-type.service';
import { PaymentMethodsStaticDataService } from '../../../services/payment-methods-static-data.service';
import { AtabexDataService } from '../../../services/atabex-data.service';
import { GoogleAnalyticsService } from '../../../services/google-analytics.service';
import { openModal, closeModal } from '../../../shared/modal-functions';
import { expirationDateIsValid, onPhoneNumberEntered, valueIsNullOrUndefined, responseIsSuccess, onCreditCardNumberEntered } from '../../../shared/helper-functions';
import { whitespaceValidator } from '../../../shared/whitespace-validator';
import { subscriptionViewSpanish, changeCollectionTypeView } from '../subscription.static.data';

@Component({
  selector: 'app-easy-pay-credit-card',
  templateUrl: './easy-pay-credit-card.component.html',
  styleUrls: ['../../../app.component.css']
})
export class EasyPayCreditCardComponent implements OnInit {
  // Static Data
  subscriptionView: any;
  changeCollectionTypeView: any;
  // Control view
  openModal: any;
  closeModal: any;
  onPhoneNumberEntered: any;
  confirmSwitchToEasyPayLoading: boolean;
  // Required Fields validation
  emptyRequiredFieldsInCreditCard: boolean;
  creditCardEmptyFields = <CreditCardRequiredFields>{};
  validExpirationDate: boolean;
  invalidPhoneNumber: boolean;
  invalidZipcode: boolean;
  termsRequired: boolean;
  // User data
  newPaymentMethodForm: FormGroup;
  newEasyPayCreditCard = <CreditCardPaymentMethod>{};
  selectedMonth: object;
  selectedYear: any;
  state: any;
  country: string;
  termsChecked: boolean;

  constructor(public formBuilder: FormBuilder, public subscriptionService: SubscriptionService, public paymentMethodsStaticDataService: PaymentMethodsStaticDataService, private changeCollectionTypeService: ChangeCollectionTypeService, public atabexDataService: AtabexDataService, public googleAnalyticsService: GoogleAnalyticsService) {
    this.subscriptionView = subscriptionViewSpanish;
    this.changeCollectionTypeView = changeCollectionTypeView;
    this.openModal = openModal;
    this.closeModal = closeModal;
    this.onPhoneNumberEntered = onPhoneNumberEntered;
  }

  ngOnInit() {
    this.confirmSwitchToEasyPayLoading = false;
    this.newEasyPayCreditCard = {
      creditCardType: undefined,
      ccExpirationMonth: undefined,
      ccExpirationYear: undefined,
    };
    this.newPaymentMethodForm = this.formBuilder.group({
      creditCardName: ['', [Validators.required, whitespaceValidator]],
      accountNumber: ['', [Validators.required]],
      creditCardCVC: ['', [Validators.required]],
      name: ['', [whitespaceValidator]],
      lastName: ['', [whitespaceValidator]],
      address1: ['', [Validators.required, whitespaceValidator]],
      address2: ['', [whitespaceValidator]],
      city: ['', [Validators.required, whitespaceValidator]],
      zipcode: ['', [Validators.required]],
      phoneNumber: ['', [Validators.required]]
    });
  }

  /*
   * Validate required fields in credit card and billing address objects
   * Validate if credit card expiration date is valid
   * Validate length of zipcode and phone number (format is being checked in the html file)
  */
  validateFields() {
    this.emptyRequiredFieldsInCreditCard = false;
    // Credit card required fields validation
    for (let key in this.newEasyPayCreditCard) {
      if (valueIsNullOrUndefined(this.newEasyPayCreditCard[key])) {
        this.creditCardEmptyFields[key] = true;
        this.emptyRequiredFieldsInCreditCard = true;
      } else {
        this.creditCardEmptyFields[key] = false;
      }
    }
    // Expiration date validation
    this.validExpirationDate = expirationDateIsValid(this.newEasyPayCreditCard.ccExpirationMonth, this.newEasyPayCreditCard.ccExpirationYear);
    // Billing address required fields validation (fields that are not part of newBillingAddressForm and therefore they don't have an Angular validator)
    if (!this.state || !this.country) {
      this.emptyRequiredFieldsInCreditCard = true;
      this.creditCardEmptyFields['state'] = true;
    }
    // Zipcode and phone number validation
    this.phoneNumber && this.phoneNumber.value.length < 14 ? this.invalidPhoneNumber = true : this.invalidPhoneNumber = false;
    this.zipcode && this.zipcode.value.length < 5 ? this.invalidZipcode = true : this.invalidZipcode = false;
    // Terms and conditions checkbox validation
    this.termsChecked ? this.termsRequired = false : this.termsRequired = true;
  }


  /*
   * Display summary for the user to confirm before executing switch to easy pay 
  */
  confirmSwitchToEasyPay() {
    this.validateFields();
    if (!this.emptyRequiredFieldsInCreditCard && this.validExpirationDate && !this.invalidZipcode && !this.invalidPhoneNumber && this.termsChecked) {
      this.confirmSwitchToEasyPayLoading = true;
      this.changeCollectionTypeService.paymentMethod = {
        creditCardName: this.creditCardName.value,
        accountNumber: this.accountNumber.value,
        creditCardCVC: this.creditCardCVC.value,
        creditCardType: this.newEasyPayCreditCard.creditCardType,
        ccExpirationMonth: this.newEasyPayCreditCard.ccExpirationMonth,
        ccExpirationYear: this.newEasyPayCreditCard.ccExpirationYear, 
        paymentTypeId: this.subscriptionService.atabexIdentifiers['creditCardId']
      };
      this.changeCollectionTypeService.billingAddress = {
        address1: this.address1.value,
        address2: this.address2.value,
        city: this.city.value,
        state: this.state,
        country: this.country,
        zipcode: this.zipcode.value,
        phoneNumber: this.phoneNumber.value
      };
      this.changeCollectionTypeService.confirmSwitchToEasyPay(this.subscriptionService.selectedSubscriptionId, this.subscriptionService.creditPromotionsId).subscribe(
        (response) => {
          this.confirmSwitchToEasyPayLoading = false;
          this.openModal('#changeCollectionTypeConfirmationModal');
      });
    }
  }

  onMonthSelected(month) {
    this.selectedMonth = month;
    this.newEasyPayCreditCard.ccExpirationMonth = month.key;
  }

  onYearSelected(year) {
    this.selectedYear = year;
    this.newEasyPayCreditCard.ccExpirationYear = year;
  }

  onStateSelected(state) {
    this.state = state;
    state['StateID'] === 'PR' ? this.country = 'Puerto Rico' : this.country = 'Estados Unidos';
  }

  detectCreditCardType(element: ElementRef) {
    this.newEasyPayCreditCard.creditCardType = onCreditCardNumberEntered(element);
  }

  submitGAEvent(eventCategory, eventAction, eventLabel) {
    this.googleAnalyticsService.emitEvent(eventCategory, eventAction, eventLabel, null);
  }

  get creditCardName() { return this.newPaymentMethodForm.get('creditCardName'); }
  get accountNumber() { return this.newPaymentMethodForm.get('accountNumber'); }
  get creditCardCVC() { return this.newPaymentMethodForm.get('creditCardCVC'); }
  get name() { return this.newPaymentMethodForm.get('name'); }
  get lastName() { return this.newPaymentMethodForm.get('lastName'); }
  get address1() { return this.newPaymentMethodForm.get('address1'); }
  get address2() { return this.newPaymentMethodForm.get('address2'); }
  get city() { return this.newPaymentMethodForm.get('city'); }
  get zipcode() { return this.newPaymentMethodForm.get('zipcode'); }
  get phoneNumber() { return this.newPaymentMethodForm.get('phoneNumber'); }

}

interface CreditCardPaymentMethod {
  creditCardType: string,
  ccExpirationMonth: string,
  ccExpirationYear: string,
}

interface CreditCardRequiredFields {
  creditCardType: boolean,
  ccExpirationMonth: boolean,
  ccExpirationYear: boolean,
  state: boolean
}




// WEBPACK FOOTER //
// /codebuild/output/src4156872633/src/src/app/components/subscription/change-collection-type/easy-pay-credit-card.component.ts